import { useEffect, useId, useRef, type ReactNode } from 'react'

const FOCAVEIS = 'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'

interface Props {
  titulo: string
  descricao?: string
  aoFechar: () => void
  children: ReactNode
  rodape?: ReactNode
}

/**
 * Diálogo modal acessível.
 *
 * Não é `<dialog>` nativo porque o comportamento de `showModal()` ainda varia entre
 * navegadores e some no ambiente de teste. O que a acessibilidade exige está aqui
 * explícito: papel e `aria-modal`, título ligado por `aria-labelledby`, foco levado
 * para dentro na abertura e devolvido ao botão de origem no fechamento, Esc fecha, e
 * Tab circula dentro do diálogo em vez de vazar para a página atrás — quem navega por
 * leitor de tela não tem como perceber que saiu do modal se ele vazar.
 *
 * Não há "X" no canto de propósito: ele seria o primeiro elemento focável e roubaria o foco
 * de abertura do primeiro campo, que é onde a pessoa precisa começar a escrever. Fechar é
 * Esc e é o botão do rodapé, que o `rodape` já traz.
 *
 * @param props título, descrição, conteúdo, rodapé e o que fazer ao fechar
 * @returns o diálogo
 */
export function Dialogo({ titulo, descricao, aoFechar, children, rodape }: Props) {
  const caixa = useRef<HTMLDivElement>(null)
  const idTitulo = useId()
  const idDescricao = useId()

  useEffect(() => {
    const anterior = document.activeElement as HTMLElement | null
    const primeiro = caixa.current?.querySelector<HTMLElement>(FOCAVEIS)
    ;(primeiro ?? caixa.current)?.focus()
    return () => anterior?.focus()
  }, [])

  useEffect(() => {
    const aoTeclar = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') {
        evento.stopPropagation()
        aoFechar()
        return
      }
      if (evento.key === 'Tab') {
        prenderFoco(evento, caixa.current)
      }
    }
    document.addEventListener('keydown', aoTeclar, true)
    return () => document.removeEventListener('keydown', aoTeclar, true)
  }, [aoFechar])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-niteroi-900/50 p-4 backdrop-blur-sm">
      <div
        ref={caixa}
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTitulo}
        aria-describedby={descricao ? idDescricao : undefined}
        tabIndex={-1}
        className="max-h-full w-full max-w-lg animate-surgir overflow-y-auto rounded-xl bg-white p-6 shadow-cartao-alto"
      >
        <h2 id={idTitulo} className="text-lg font-semibold text-slate-900">
          {titulo}
        </h2>
        {descricao && (
          <p id={idDescricao} className="mt-1.5 text-sm text-slate-600">
            {descricao}
          </p>
        )}
        <div className="mt-5">{children}</div>
        {rodape && <div className="mt-6 flex justify-end gap-2 border-t border-slate-100 pt-4">{rodape}</div>}
      </div>
    </div>
  )
}

function prenderFoco(evento: KeyboardEvent, caixa: HTMLElement | null): void {
  if (!caixa) {
    return
  }
  const alvos = Array.from(caixa.querySelectorAll<HTMLElement>(FOCAVEIS))
  if (alvos.length === 0) {
    return
  }
  const primeiro = alvos[0]
  const ultimo = alvos[alvos.length - 1]
  const ativo = document.activeElement

  if (evento.shiftKey && (ativo === primeiro || ativo === caixa)) {
    evento.preventDefault()
    ultimo?.focus()
  } else if (!evento.shiftKey && ativo === ultimo) {
    evento.preventDefault()
    primeiro?.focus()
  }
}
